import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Params } from '@angular/router';
import { DatePipe } from '@angular/common';
import { forkJoin } from 'rxjs';
import { Orders, ProgressObjective } from 'src/app/models/Orders.model';
import { OrdersService } from 'src/app/services/orders.service';
import { ProductsService } from 'src/app/services/products.service';
import { MessagesService } from 'src/app/services/messages.service';
import { SearchServiceService } from 'src/app/services/shared/search-service.service';
// 🚨 v2.14.1 T2b (2026-07-09): LabelSettings dropped — era usato solo
// per il field `setPercentLabel` che non è mai referenziato in template.
// Dead type + ProgressBarModule droppato da app.module.ts.
// import { LabelSettings } from '@progress/kendo-angular-progressbar';
// v2.14.2: `OrderTotalService` (import-orders.service) rimosso da develop
// come parte del cleanup dead code (item #188 del Phase 2 Kickoff:
// "Delete OrderTotalService write-only dead"). Il service era iniettato
// ma mai chiamato. Import + DI rimossi qui in v2.14.3 dopo il merge di
// develop nel branch — ha esposto l'inconsistenza.
import { GoalService } from 'src/app/services/goal.service';
// v2.14.3: rimossi import di MonthlyGoalService + GoalDataService
// (dead code post-cleanup obiettivi localStorage → BE via
// `/objective_progress`).
import { Log } from 'src/app/services/shared/log';
import confetti from 'canvas-confetti';

// ─── Shape of the order-status counters returned by getInfoOrders ────
interface IInforOrders {
  t_or_new: number;
  t_or_partial: number;
  t_or_cm: number;
  t_or_prepare: number;
  t_or_to_sign: number;
  t_or_completed: number;
  t_or_delivery: number;
}

class InfoOrders implements IInforOrders {
  t_or_new: number = -1;
  t_or_partial: number = -1;
  t_or_cm: number = -1;
  t_or_prepare: number = -1;
  t_or_to_sign: number = -1;
  t_or_completed: number = -1;
  t_or_delivery: number = -1;

  constructor(orderInfo: IInforOrders | null) {
    if (!orderInfo) return;
    // 🚨 FIX v2.14.2 (bug audit #5): guardia `?? -1` per gestire il
    // caso in cui il BE risponde con oggetto parziale (uno o più
    // campi undefined). Senza guardia il template mostrava
    // "undefined" o vuoto al PM invece della sentinella -1 che
    // triggera lo skeleton loading. La riga 39-46 imposta i default
    // ma il constructor sovrascrive con undefined se il campo manca.
    this.t_or_new = orderInfo.t_or_new ?? -1;
    this.t_or_partial = orderInfo.t_or_partial ?? -1;
    this.t_or_cm = orderInfo.t_or_cm ?? -1;
    this.t_or_prepare = orderInfo.t_or_prepare ?? -1;
    this.t_or_to_sign = orderInfo.t_or_to_sign ?? -1;
    this.t_or_completed = orderInfo.t_or_completed ?? -1;
    this.t_or_delivery = orderInfo.t_or_delivery ?? -1;
  }
}

interface IInfo {
  t_or_bordero: number;
  t_message: number;
  t_products: number;
  t_account: number;
  t_agents: number;
}

class Info implements IInfo {
  t_or_bordero: number = -1;
  t_message: number = -1;
  t_products: number = -1;
  t_account: number = -1;
  t_agents: number = -1;

  constructor(info: IInfo | null) {
    if (!info) return;
    // 🚨 FIX v2.14.2 (bug audit #5): guardia `?? -1` come per InfoOrders.
    this.t_or_bordero = info.t_or_bordero ?? -1;
    this.t_message = info.t_message ?? -1;
    this.t_products = info.t_products ?? -1;
    this.t_account = info.t_account ?? -1;
    this.t_agents = info.t_agents ?? -1;
  }
}

/**
 * Restituisce la data corrente formattata come `yyyy-MM-dd` in
 * timezone LOCALE (non UTC). Fondamentale per Diakonix perché il
 * BE serializza `or_closed_date` con offset locale (+02:00 estate),
 * ma `Date.prototype.toISOString()` normalizza a UTC → confronti
 * `dateStr === todayStr` falliscono a cavallo di mezzanotte locale.
 *
 * Esempi:
 *   - Ora locale 2026-07-10 01:30 (CEST +02) → "2026-07-10"
 *   - Con `toISOString().slice(0,10)` sarebbe "2026-07-09" ❌
 *
 * 🚨 FIX v2.14.2 (bug audit #1 + #2): pre-fix, dashboard usava
 * `new Date(x).toISOString().slice(0,10)` mescolato con
 * `getFullYear/getMonth` locali → ordini a cavallo di mezzanotte
 * finivano nel giorno/mese sbagliato → discrepanza sistematica col
 * CRM del PM.
 */
function localDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Mutually-exclusive search mode for the top filter panel. */
type SearchMode = '' | 'order' | 'cliente' | 'notes' | 'tracking';

/**
 * Dashboard — admin portal landing page.
 *
 * Section organization (2026-05-25):
 *   - "🔎 Cerca ordini" — search filters by mode + date.
 *   - "📋 Operazioni in corso" — 6 phase counters (always all-time).
 *   - "🎯 Obiettivi → Oggi"  — 4 fixed cards driven by today / current
 *     month: monthly goal, dynamic daily goal, end-of-month forecast,
 *     year-to-date.
 *   - "📅 Riepilogo storico" — 2 cards driven by selectors: monthly
 *     and daily lookup of past earnings.
 *
 * Key design rule: the "Oggi" subsection never reacts to the
 * historical pickers. The historical pickers never touch global
 * goalService state.
 */
@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: false,
})


export class DashboardComponent implements OnInit, OnDestroy {

  searchService = inject(SearchServiceService);
  private destroyRef = inject(DestroyRef);

  // ─── UI state ───────────────────────────────────────────────────
  loading: boolean = false;
  firstEnteredOnPage: boolean = false;
  searchMode: SearchMode = '';

  // ─── Counters (6 ops cards) ─────────────────────────────────────
  orderInfo: InfoOrders = new InfoOrders(null);
  otherInfo: Info = new Info(null);

  // ─── Search ─────────────────────────────────────────────────────
  search: string = '';
  searchType: string = '';
  formattedDate: string = '';
  dynamicLink: Params = {};
  endDate: Date = new Date();

  // ─── Today's calendar (NEVER changed by selectors, ma ricalcolato
  //     ad ogni recomputeAllSums per gestire tab lasciata aperta a
  //     cavallo di mezzanotte)
  //
  // 🚨 FIX v2.14.2 (bug audit #1): rimosso `readonly` — i valori
  // erano congelati al costruttore. Dashboard aperta alle 23:59 di
  // ieri e lasciata aperta oggi mostrava dati del giorno prima
  // finché non si ricaricava manualmente. Ora `refreshTodayFields()`
  // è chiamato in cima a `recomputeAllSums()` e allinea i 4 campi
  // alla data locale corrente.
  //
  // Inoltre: `todayStr` ora usa `localDateStr()` (data locale)
  // invece che `toISOString()` (UTC) — fix di sistema per la
  // discrepanza timezone (bug #2).
  today: Date = new Date();
  todayMonth: number = this.today.getMonth();
  todayYear: number = this.today.getFullYear();
  todayStr: string = localDateStr(this.today);

  // ─── "Oggi" state (Card 1, 2, 5, 6) ─────────────────────────────
  /** Monthly objective for the CURRENT calendar month (always). */
  currentMonthGoal: number = 0;
  /** Earned so far in the current month. */
  currentMonthEarned: number = 0;
  /** Progress to current month's goal, as %. */
  currentMonthProgress: number = 0;

  /** Dynamic daily target for TODAY (refreshes as the month progresses). */
  todayDailyGoal: number = 0;
  /** Earned today. */
  todayEarned: number = 0;
  /** Progress to today's daily target, as %. */
  todayDailyProgress: number = 0;

  /** Projected end-of-month total based on current daily pace. */
  forecastEndOfMonth: number = 0;
  /** Forecast as % of monthly goal. */
  forecastPercent: number = 0;

  /** Earned year-to-date (Jan 1 → today). */
  ytdEarned: number = 0;
  /** Sum of the 12 monthly objectives for the current year. */
  ytdYearlyGoal: number = 0;
  /** YTD as % of yearly objective. */
  ytdPercent: number = 0;

  // ─── "Storico" state (Card 3, 4) ────────────────────────────────
  /** Year selected via the historical lookup picker (default = today). */
  selectedYear: number = this.todayYear;
  /** Month selected via the historical lookup picker (default = today). */
  selectedMonth: number = this.todayMonth;
  /** Date selected via the historical lookup picker (ISO yyyy-mm-dd). */
  selectedDate: string | null = this.todayStr;

  /** Earned in the selectedMonth/selectedYear (independent of "Oggi"). */
  selectedMonthEarned: number = 0;
  /** Earned on selectedDate. */
  selectedDateEarned: number = 0;

  // ─── Misc ───────────────────────────────────────────────────────
  /** Cache of the closed-orders array; recomputed once per fetch. */
  private closedOrdersCache: Orders.IOrderClosed[] = [];

  /**
   * Fix 2026-07-08: cache di ordini "to sign" (preparati, in attesa
   * firma) e "open" (in ogni stato != Assegnato). Servono per due
   * cose:
   *   1. Contare i "preparati oggi/mese/anno" — la nuova semantica di
   *      currentMonthEarned/todayEarned/ytdEarned deve riflettere il
   *      lavoro effettivo dell'operatore, non solo i chiusi (i corrieri
   *      passano tipicamente a fine giornata → dashboard restava a
   *      quasi zero per tutto il turno, come già fatto nel warehouse
   *      monitor v2.12.9 T2c).
   *   2. Recuperare or_net_value degli ordini to-sign — il BE
   *      restituisce sempre 0 per l'endpoint to-sign, ma lo stesso
   *      ordine in openOrders ha il valore corretto.
   */
  private ordersToSignCache: Orders.OrderSignature[] = [];
  private openOrdersCache: Orders.IOrderOpenList[] = [];

  hasCelebratedMonthly = false;
  hasCelebratedDaily = false;

  /** Orders grid (top-of-page net total for the user's own session). */
  public gridData: { or_net_value: number; [key: string]: any }[] = [];
  netValue: number = 0;
  etValueTotal: number = 0;
  etValuePartial: number = 0;
  etValueBlocked: number = 0;
  etMonthlyOrders: number = 0;

  /** Reentrancy guard for goalService writes. */
  private isUpdatingGoals = false;

  // 🚨 v2.14.1 T2b (2026-07-09): field `setPercentLabel` (LabelSettings)
  // droppato — non era mai referenziato in template. Se serve in futuro,
  // re-add + reintrodurre `ProgressBarModule` in app.module.ts.

  months = [
    { name: 'Gennaio', value: 0 },
    { name: 'Febbraio', value: 1 },
    { name: 'Marzo', value: 2 },
    { name: 'Aprile', value: 3 },
    { name: 'Maggio', value: 4 },
    { name: 'Giugno', value: 5 },
    { name: 'Luglio', value: 6 },
    { name: 'Agosto', value: 7 },
    { name: 'Settembre', value: 8 },
    { name: 'Ottobre', value: 9 },
    { name: 'Novembre', value: 10 },
    { name: 'Dicembre', value: 11 },
  ];

  years: number[] = (() => {
    const out: number[] = [];
    for (let y = 2021; y <= new Date().getFullYear(); y++) out.push(y);
    return out;
  })();

  constructor(
    private ordersServ: OrdersService,
    private stockSrv: ProductsService,
    public productSrv: ProductsService,
    private messagesServ: MessagesService,
    // v2.14.3 (post-merge develop): rimossa DI di OrderTotalService
    // (service eliminato da develop, cfr. commento import in cima).
    // `golServ` (GoalService) rimane vivo: fornisce `getWorkingDays()`
    // usato dal calcolo forecast/dailyTarget dinamico (FE-side).
    private golServ: GoalService,
    // v2.14.3: rimosse DI di `MonthlyGoalService` (goalService) e
    // `GoalDataService` (goalDataService) — erano legate al vecchio
    // flusso localStorage-based ora sostituito da `/objective_progress`.
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // 🚨 FIX v2.14.3 (revised): la Dashboard consuma direttamente
    // l'endpoint `/objective_progress` — stesso pattern del warehouse
    // monitor. Il BE calcola già i valori aggregati (progress + total
    // + percentage) su daily / monthly / yearly, quindi non c'è più
    // bisogno di:
    //   - leggere obiettivi dalla localStorage (goalDataService)
    //   - filtrare/sommare 12 record mensili lato FE
    //   - passare per goalDataService.objectives$ subscribe
    // Il vecchio flusso via `getObjectives` (endpoint di Settings) è
    // stato scartato dietro indicazione del leader BE (Robert):
    // `/objective_progress` è l'endpoint dedicato al display, quello
    // che la Dashboard deve usare.
    this.loadObjectiveProgress();

    this.getNewOrders();
    this.loadAllOrderTotals();
    // Fix 2026-07-08 (rivisitato in v2.14.9): fetch anche to-sign +
    // open per la nuova semantica "preparato oggi/mese/anno" (vedi
    // commento su cache above).
    //
    // Ruoli dei due sistemi post-v2.14.9:
    //   • `/objective_progress` (BE) → source-of-truth per gli
    //     OBIETTIVI (monthly_total, yearly_total). I *_progress dal
    //     BE sono usati solo come fallback iniziale finché il FE non
    //     ha computato (vedi `hasComputedFromFE`).
    //   • `recomputeAllSums()` (FE) → source-of-truth per i PROGRESS
    //     (currentMonthEarned, todayEarned, ytdEarned) con la
    //     semantica "preparato = closed + da-firmare" — coerente col
    //     Warehouse Monitor (v2.12.9 T2c). Necessario perché il BE
    //     `/objective_progress` conta solo status "completed"
    //     definitivi, mentre gli operatori considerano "preparato"
    //     anche gli ordini in stato "Da firmare".
    //
    // Gli storici (selectedMonthEarned, selectedDateEarned) restano
    // sempre calcolati lato FE dai closed orders.
    this.loadOrdersToSignAndOpen();
    this.loadInitialDashboardInfo();
  }

  /**
   * Source of truth per gli obiettivi Diakonix (fatturato).
   * Popolato da `loadObjectiveProgress()` (endpoint `/objective_progress`).
   *
   * Il modello `ProgressObjective` espone campi come stringhe
   * (convenzione BE: valori numerici con precisione decimale trasmessi
   * come string per evitare perdita di precisione JS). La conversione
   * a Number avviene in `applyProgressToKpi()`.
   *
   * Coerente col pattern usato dal warehouse monitor
   * (`WarehouseOrdersComponent.progress`).
   */
  progress: ProgressObjective | null = null;

  /**
   * Flag: `recomputeAllSums()` ha già computato almeno una volta con
   * i cache FE (closedOrdersCache/ordersToSignCache/openOrdersCache).
   *
   * 🚨 FIX v2.14.9 (PM feedback 2026-07-21): la Dashboard mostrava
   * valori € / % DIVERSI dal Warehouse Monitor. Root cause: il BE
   * `/objective_progress` aggrega i `*_progress` conteggiando solo
   * ordini in status "completed" definitivo (escludendo "Da firmare"),
   * mentre il Warehouse Monitor e la nuova semantica FE del 2026-07-08
   * ("preparato oggi/mese/anno") includono anche "Da firmare" — perché
   * questi sono ordini che l'operatore ha già completato, in attesa
   * di firma del corriere.
   *
   * Fix: `applyProgressToKpi()` copia i `*_total` (obiettivi) dal BE
   * ma i `*_progress` solo come FALLBACK iniziale, finché
   * `recomputeAllSums()` non produce i valori corretti FE. Da quel
   * momento in poi (`hasComputedFromFE = true`), i valori BE
   * non vengono più copiati sui field template — la Dashboard resta
   * coerente col Warehouse Monitor.
   */
  private hasComputedFromFE = false;

  /**
   * Fetch degli obiettivi + progress dal BE via `GET /objective_progress`.
   *
   * Il payload contiene già i valori aggregati calcolati lato BE:
   *   - `monthly_total`  → obiettivo mensile (currentMonthGoal)
   *   - `monthly_progress` → guadagnato nel mese (currentMonthEarned)
   *   - `daily_total`    → obiettivo giornaliero statico BE (usato
   *                        SOLO come fallback; il target daily
   *                        dinamico viene ricalcolato lato FE)
   *   - `daily_progress` → guadagnato oggi (todayEarned)
   *   - `yearly_total`   → obiettivo annuale (ytdYearlyGoal)
   *   - `yearly_progress` → guadagnato YTD (ytdEarned)
   *   - `daily_percentage`, `monthly_percentage` → percentuali
   *     precalcolate BE (attualmente non usate; il FE ricalcola
   *     via safePercent per coerenza con forecastPercent/ytdPercent).
   *
   * ─── Fallback ────────────────────────────────────────────
   * Se il BE fallisce o `response.success=false`, i field restano
   * ai valori calcolati da `recomputeAllSums()` (closed + to-sign
   * client-side). Il template mostrerà comunque qualcosa di
   * ragionevole invece di zero.
   *
   * ─── Debug logging ───────────────────────────────────────
   * I `Log.debug(...)` sotto rispettano il log level → silenziati
   * automaticamente in produzione. Non richiedono cleanup pre-release.
   *
   * Ref: coordinato con leader BE (Robert) 2026-07-13.
   */
  private loadObjectiveProgress(): void {
    Log.debug('[Dashboard] loadObjectiveProgress() START — GET /objective_progress');
    this.ordersServ.getObjectiveProgress()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          Log.debug(
            '[Dashboard] /objective_progress response →',
            'success:', response?.success,
            '| data:', response?.data,
          );
          if (!response?.success || !response.data) {
            Log.warn(
              '[Dashboard] objective_progress: no success/data — tengo i valori calcolati FE',
            );
            return;
          }
          this.progress = new ProgressObjective(response.data);
          this.applyProgressToKpi();
          this.cdr.detectChanges();
        },
        error: (err) => {
          Log.warn(
            '[Dashboard] objective_progress failed, fallback su recomputeAllSums FE',
            err,
          );
        },
      });
  }

  /**
   * Copia i campi da `this.progress` (ProgressObjective, stringhe)
   * verso i field numerici usati dai template delle 4 card obiettivo.
   * Chiamato subito dopo che `/objective_progress` risponde con
   * `success: true`.
   *
   * Sanitize: BE ritorna "-1" come sentinel di "non impostato"; la
   * funzione `toNum` lo mappa a 0. Anche stringhe malformate,
   * `undefined` o non-numeriche vengono normalizzate a 0.
   *
   * Effetto collaterale: chiama `recomputeAllProgress()` che ricalcola
   * le derived (currentMonthProgress, todayDailyProgress,
   * forecastEndOfMonth, forecastPercent, ytdPercent, todayDailyGoal
   * dinamico). Il forecast e il daily target dinamico restano
   * responsabilità FE (dipendono dai giorni lavorativi del calendario).
   */
  private applyProgressToKpi(): void {
    if (!this.progress) return;
    const toNum = (v: string | undefined): number => {
      if (!v) return 0;
      const n = Number(v);
      return Number.isFinite(n) && n > 0 ? n : 0;
    };

    // Obiettivi: sempre BE-driven — sono valori di configurazione
    // (non business logic), il BE è la source-of-truth.
    this.currentMonthGoal = toNum(this.progress.monthly_total);
    this.ytdYearlyGoal = toNum(this.progress.yearly_total);

    // Progress: usati SOLO come fallback iniziale, finché
    // `recomputeAllSums()` non ha computato con la nuova semantica
    // (preparato = closed + da-firmare, coerente col warehouse monitor).
    //
    // 🚨 FIX v2.14.9 (PM feedback 2026-07-21): pre-fix questi 3 field
    // venivano sempre sovrascritti dai valori BE. Il BE
    // `/objective_progress` conteggia solo status "completed" definitivi,
    // escludendo "Da firmare" → Dashboard mostrava valori inferiori
    // al Warehouse Monitor (che usa la semantica preparato+dafirmare).
    // Vedi doc su `hasComputedFromFE` per il razionale completo.
    if (!this.hasComputedFromFE) {
      this.currentMonthEarned = toNum(this.progress.monthly_progress);
      this.todayEarned = toNum(this.progress.daily_progress);
      this.ytdEarned = toNum(this.progress.yearly_progress);
    }

    Log.debug(
      '[Dashboard] applyProgressToKpi →',
      'monthGoal:', this.currentMonthGoal,
      '| monthEarned:', this.currentMonthEarned,
      '| todayEarned:', this.todayEarned,
      '| ytdEarned:', this.ytdEarned,
      '| ytdYearlyGoal:', this.ytdYearlyGoal,
      '| hasComputedFromFE:', this.hasComputedFromFE,
    );
    this.recomputeAllProgress();
  }

  /**
   * Fetch parallelo di `getOrdersToSign()` e `getOpenOrders()`.
   * Popola i cache che alimentano `recomputeAllSums` (nuova semantica
   * "preparato oggi/mese/anno" + join per or_net_value).
   *
   * Se una delle fetch fallisce, degrada gracefully: i cache
   * restano vuoti e i sum si comportano come pre-fix (solo chiusi).
   */
  private loadOrdersToSignAndOpen(): void {
    this.ordersServ.getOrdersToSign()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (orders) => {
          this.ordersToSignCache = orders ?? [];
          this.recomputeAllSums();
          this.cdr.detectChanges();
        },
        error: (err) => Log.warn('[Dashboard] getOrdersToSign failed', err),
      });

    this.ordersServ.getOpenOrders()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (orders) => {
          this.openOrdersCache = orders ?? [];
          this.recomputeAllSums();
          this.cdr.detectChanges();
        },
        error: (err) => Log.warn('[Dashboard] getOpenOrders failed', err),
      });
  }

  /**
   * Recupera il valore € di un ordine anche quando il BE restituisce 0
   * (caso tipico degli ordini "to sign", dove il totale è calcolato
   * al momento della firma). Stesso pattern del warehouse monitor
   * (v2.14.x).
   *
   * @param allowZero se `true`, un valore `or_net_value === 0`
   *                  è considerato legittimo e NON triggera il
   *                  lookup nelle cache. Usato per closed orders
   *                  dove uno 0 significa realmente "ordine omaggio"
   *                  (promo, sample, sostituzione gratuita) e non
   *                  deve essere "corretto" con valori residui
   *                  della openOrdersCache.
   *                  Default: `false` (comportamento originale, ok
   *                  per to-sign / preparazione).
   *
   * 🚨 FIX v2.14.2 (bug audit #3): pre-fix, la funzione trattava
   * `direct === 0` come "valore mancante" e faceva il lookup in
   * open+closed cache SEMPRE. Per ordini closed a valore 0
   * legittimo (omaggi/promo), se lo stesso `order_id` esisteva
   * nella openOrdersCache da un vecchio ciclo di vita, il totale
   * mensile veniva inquinato dal valore precedente.
   *
   * Lookup order:
   *   1. `order.or_net_value` diretto (se allowZero=true accetta 0)
   *   2. Match in `openOrdersCache` per `order_id` (fix principale)
   *   3. Match in `closedOrdersCache` per `order_id` (safety net)
   *   4. Fallback 0
   */
  private orderValueOf(
    order: { order_id?: number | string; or_net_value?: number | string | null },
    allowZero: boolean = false,
  ): number {
    const direct = typeof order?.or_net_value === 'string'
      ? Number(order.or_net_value)
      : (order?.or_net_value ?? 0);
    if (Number.isFinite(direct)) {
      // Se allowZero è true, uno 0 legittimo è accettato senza lookup.
      // Altrimenti (to-sign), fallback su cache lookup come prima.
      if (allowZero || direct !== 0) return direct as number;
    }
    const oid = order?.order_id;
    if (oid === undefined || oid === null) return 0;
    const openMatch = this.openOrdersCache.find((o) => o.order_id === oid);
    if (openMatch?.or_net_value) return Number(openMatch.or_net_value) || 0;
    const closedMatch = this.closedOrdersCache.find((o) => o.order_id === oid);
    if (closedMatch?.or_net_value) return Number(closedMatch.or_net_value) || 0;
    return 0;
  }

  ngOnDestroy(): void {
    // `takeUntilDestroyed` covers the subscribes.
  }

  // ─── ALL-TIME sentinel for the 6 operation counters ─────────────
  private readonly ALL_TIME_DATE = '2010-01-01T00:00:00';

  private loadInitialDashboardInfo(): void {
    if (this.firstEnteredOnPage) return;

    forkJoin({
      orders: this.ordersServ.getInfoOrders('5', this.ALL_TIME_DATE, ''),
      dateInfo: this.ordersServ.getInfo(this.ALL_TIME_DATE),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ orders, dateInfo }) => {
          if (orders) {
            this.orderInfo = new InfoOrders(orders);
            this.firstEnteredOnPage = true;
          }
          if (dateInfo) this.otherInfo = new Info(dateInfo);
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          Log.error('[Dashboard] loadInitialDashboardInfo failed', err);
          this.loading = false;
          this.cdr.detectChanges();
        },
      });

    this.initEndDate();
  }

  // ─── Goals ────────────────────────────────────────────────────
  //
  // 🚨 CLEANUP v2.14.3: rimosso `refreshGoalsForCurrentMonthAndYear()`
  // (~50 righe) — leggeva monthly / yearly goals dalla localStorage
  // via `goalDataService`. Ora la Dashboard consuma direttamente
  // `/objective_progress` dal BE (vedi `loadObjectiveProgress`), che
  // fornisce sia `monthly_total` che `yearly_total` precalcolati con
  // sanitize lato BE. Il sanity check ">10M" e la deduplicazione
  // "Annuale" del vecchio metodo sono ora responsabilità del BE.
  //
  // Dead code rimosso in questo giro:
  //   - `refreshGoalsForCurrentMonthAndYear()`
  //   - `goalDataService.initObjectivesFromStorage()` in ngOnInit
  //   - subscribe a `goalDataService.objectives$` in ngOnInit
  //   - dipendenza DI `GoalDataService` + `MonthlyGoalService`
  //     (rimosse dal constructor se non usate altrove).
  //
  // Se il PM cambia gli obiettivi in Settings e la Dashboard è già
  // aperta in un'altra tab, ora NON riceve l'evento real-time (era
  // via `objectives$` in-memory). Al prossimo refresh / navigazione
  // verso Dashboard, il fetch `/objective_progress` pesca i valori
  // aggiornati dal BE. Se serve real-time, aggiungere polling ~30s
  // come warehouse-orders (backlog).

  // ─── Order totals — single fetch → cache → recompute all sums ──
  /**
   * Single fetch of `getClosedOrders` populating an in-memory cache.
   * All five sums (today, current month, YTD, selected month,
   * selected date) are derived in one pass via `recomputeAllSums`.
   * Selector changes only re-derive from the cache — no extra API
   * calls.
   */
  /**
   * 🚨 FIX 2026-06-26 (Phase 2 Sprint 1 W1 day 4 hotfix): NG0100
   * ExpressionChangedAfterItHasBeenCheckedError emerso post-cleanup
   * OrderTotalService (W1 day 3). Bug latente pre-esistente: il
   * cleanup ha cambiato il timing del DI graph, esponendo il pattern.
   * Template line 295 leggeva `ytdEarned = 0` (init), poi
   * `recomputeAllSums()` cambiava a valore reale (es. 4069.89)
   * nel medesimo CD cycle → NG0100. **6a ricorrenza del CD-bug
   * pattern post-subscribe** dopo v2.11.1 J.3, v2.11.2 Q4, v2.11.2
   * Q6, v2.12.0 Q7, v2.12.2 change-operator, v2.12.4 B1.
   * Lint rule custom (Phase 2 Sprint 1 W2 item #2) preventerà
   * ricorrenze future. Fix: cdr.detectChanges() post next + error.
   */
  private loadAllOrderTotals(): void {
    this.ordersServ.getClosedOrders()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (orders) => {
          this.closedOrdersCache = orders ?? [];
          this.recomputeAllSums();
          this.cdr.detectChanges();
        },
        error: (err) => {
          Log.error('[Dashboard] getClosedOrders failed', err);
          this.cdr.detectChanges();
        },
      });
  }

  /**
   * Riallinea `today*` alla data locale corrente. Chiamato in cima
   * a `recomputeAllSums()` per gestire il caso "dashboard lasciata
   * aperta a cavallo di mezzanotte" (bug audit #1).
   *
   * Idempotente e economico (4 assegnazioni). Ogni fetch triggera
   * recomputeAllSums → dashboard resta allineata.
   */
  private refreshTodayFields(): void {
    this.today = new Date();
    this.todayMonth = this.today.getMonth();
    this.todayYear = this.today.getFullYear();
    this.todayStr = localDateStr(this.today);
  }

  private recomputeAllSums(): void {
    // Sync date "oggi" prima di ogni ricalcolo — fix bug audit #1.
    this.refreshTodayFields();

    let currentMonthSum = 0;
    let todaySum = 0;
    let ytdSum = 0;
    let selectedMonthSum = 0;
    let selectedDateSum = 0;

    // ─── Chiusi (source of truth per selectedMonth/Date storici) ──
    for (const order of this.closedOrdersCache) {
      if (!order.or_closed_date) continue;
      const date = new Date(order.or_closed_date);
      if (Number.isNaN(date.getTime())) continue;
      // Per closed orders: `allowZero: true` — ordini omaggio o
      // promozione hanno legittimamente 0€ e NON devono innescare
      // il lookup nella openOrdersCache (bug audit #3, che
      // recuperava valori residui delle fasi precedenti).
      const value = this.orderValueOf(order, true);
      // 🚨 FIX v2.14.2 (bug audit #2): usiamo localDateStr invece che
      // toISOString().slice(0,10) — coerente con `y/m` che sono già
      // locali. Senza questo, ordini a cavallo di mezzanotte
      // finivano contati nel giorno/mese sbagliato.
      const dateStr = localDateStr(date);
      const y = date.getFullYear();
      const m = date.getMonth();

      if (y === this.todayYear) {
        ytdSum += value;
        if (m === this.todayMonth) currentMonthSum += value;
      }
      if (dateStr === this.todayStr) todaySum += value;
      if (y === this.selectedYear && m === this.selectedMonth) selectedMonthSum += value;
      if (this.selectedDate && dateStr === this.selectedDate) selectedDateSum += value;
    }

    // ─── "To sign" preparati (Fix 2026-07-08) ─────────────────────
    // Nuova semantica per today/currentMonth/YTD: includiamo anche
    // gli ordini che gli operatori hanno preparato ma non ancora
    // firmati dal corriere. Usa `or_assigned_date` come proxy per
    // "data preparazione" (stesso trade-off del warehouse monitor
    // v2.12.9 T2c). Non tocca selectedMonth/Date (rimangono storici,
    // basati su or_closed_date).
    for (const order of this.ordersToSignCache) {
      if (!order?.or_assigned_date) continue;
      const date = new Date(order.or_assigned_date);
      if (Number.isNaN(date.getTime())) continue;
      // Per to-sign: `allowZero: false` (default) — il BE spesso
      // manda `or_net_value = 0` per to-sign e serve fare il lookup
      // in cache per recuperare il valore reale.
      const value = this.orderValueOf(order);
      // Local date string (fix timezone come sopra).
      const dateStr = localDateStr(date);
      const y = date.getFullYear();
      const m = date.getMonth();

      if (y === this.todayYear) {
        ytdSum += value;
        if (m === this.todayMonth) currentMonthSum += value;
      }
      if (dateStr === this.todayStr) todaySum += value;
    }

    this.currentMonthEarned = currentMonthSum;
    this.todayEarned = todaySum;
    this.ytdEarned = ytdSum;
    this.selectedMonthEarned = selectedMonthSum;
    this.selectedDateEarned = selectedDateSum;

    // 🚨 FIX v2.14.9: segnala che i valori sono ora "FE source-of-truth".
    // `applyProgressToKpi()` da qui in poi NON sovrascriverà più i
    // *_progress con quelli del BE (che escluderebbero i "Da firmare").
    // Vedi doc su `hasComputedFromFE`.
    this.hasComputedFromFE = true;

    this.recomputeAllProgress();
  }

  /**
   * Recomputes all the derived numbers shown in the "Oggi" cards.
   * Cheap (no fetches) — call after every sum / goal change.
   */
  private recomputeAllProgress(): void {
    // Card 1: monthly progress
    this.currentMonthProgress = this.safePercent(this.currentMonthEarned, this.currentMonthGoal);

    // Card 2: dynamic daily target + today's progress
    this.todayDailyGoal = this.computeDynamicDailyGoal();
    this.todayDailyProgress = this.safePercent(this.todayEarned, this.todayDailyGoal);

    // Card 5: forecast at end-of-month
    this.updateForecast();

    // Card 6: YTD progress
    this.ytdPercent = this.safePercent(this.ytdEarned, this.ytdYearlyGoal);

    // Confetti latches.
    if (this.currentMonthProgress < 100) this.hasCelebratedMonthly = false;
    if (this.todayDailyProgress < 100) this.hasCelebratedDaily = false;
    if (!this.hasCelebratedMonthly && this.currentMonthProgress >= 100) {
      this.hasCelebratedMonthly = true;
      this.launchConfetti();
    }
    if (!this.hasCelebratedDaily && this.todayDailyProgress >= 100) {
      this.hasCelebratedDaily = true;
      this.launchConfetti();
    }
  }

  /**
   * Today's daily target = (monthly goal - already earned) / remaining
   * working days. Always anchored on TODAY's calendar — never on the
   * historical picker.
   */
  private computeDynamicDailyGoal(): number {
    const remaining = this.currentMonthGoal - this.currentMonthEarned;
    if (remaining <= 0 || !Number.isFinite(remaining)) return 0;

    const totalWorkDays =
      this.golServ.getWorkingDays(this.todayYear, this.todayMonth) ||
      this.weekdaysInMonth(this.todayYear, this.todayMonth);
    const pastWorkDays = this.countWorkingDaysPassed(
      this.todayYear,
      this.todayMonth,
      this.today.getDate() - 1,
    );
    const remainingDays = Math.max(1, totalWorkDays - pastWorkDays);
    return Math.max(0, Math.round(remaining / remainingDays));
  }

  /**
   * Projects end-of-month total based on the daily average so far
   * this month: `dailyAvg × totalWorkdaysOfMonth`. Comparing this
   * to the monthly goal gives the "if we keep this pace" view.
   *
   * 🚨 FIX v2.14.2 (bug audit #4): pre-fix passavamo
   * `this.today.getDate()` a `countWorkingDaysPassed` — che include
   * oggi. Alle 08:00 di lunedì con 1000€ chiusi, il calcolo era
   * 1000/4 (invece che 1000/3) → dailyAvg diluito → forecast
   * sottostimato del ~25% per tutta la mattina. Ora escludiamo
   * oggi (`getDate() - 1`), coerente con `computeDynamicDailyGoal`.
   */
  private updateForecast(): void {
    const totalWorkDays =
      this.golServ.getWorkingDays(this.todayYear, this.todayMonth) ||
      this.weekdaysInMonth(this.todayYear, this.todayMonth);
    // Escludiamo oggi dal conteggio dei giorni "passati" — oggi
    // non è ancora completato, contarlo come giorno pieno diluisce
    // la media e sottostima il forecast.
    const pastWorkDays = this.countWorkingDaysPassed(
      this.todayYear,
      this.todayMonth,
      this.today.getDate() - 1,
    );
    if (pastWorkDays <= 0) {
      this.forecastEndOfMonth = 0;
      this.forecastPercent = 0;
      return;
    }
    const dailyAvg = this.currentMonthEarned / pastWorkDays;
    this.forecastEndOfMonth = Math.round(dailyAvg * totalWorkDays);
    this.forecastPercent = this.safePercent(this.forecastEndOfMonth, this.currentMonthGoal);
  }

  // ─── End-date picker for the search bar ────────────────────────
  private initEndDate(): void {
    this.endDate = new Date();
    this.endDate.setFullYear(this.endDate.getFullYear() - 1);
    this.endDate.setHours(0, 0, 0, 0);
    this.updateFormattedDate();
  }

  updateFormattedDate(): void {
    this.formattedDate =
      new DatePipe('en-US').transform(this.endDate, 'yyyy-MM-ddTHH:mm:ss') ?? '';
  }

  /** Back-compat shim — legacy template might still bind to checkData(). */
  checkData(): void {
    this.updateFormattedDate();
  }

  // ─── Search panel ───────────────────────────────────────────────
  toggleSearchMode(mode: Exclude<SearchMode, ''>): void {
    this.searchMode = this.searchMode === mode ? '' : mode;
    switch (this.searchMode) {
      case 'order':    this.searchType = '1'; break;
      case 'cliente':  this.searchType = '2'; break;
      case 'notes':    this.searchType = '3'; break;
      case 'tracking': this.searchType = '4'; break;
      default:         this.searchType = '';
    }
  }

  /** Back-compat shim for any legacy template using `checkBoolean('1')`. */
  checkBoolean(searchType: string): void {
    this.searchType = searchType;
  }

  searchInfoOrders(startDate: string, search: string): void {
    this.loading = true;
    this.orderInfo = new InfoOrders(null);
    this.otherInfo = new Info(null);
    if (!search || !this.searchType) this.searchType = '5';

    this.ordersServ.getInfoOrders(this.searchType, startDate, search)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          if (!data) { this.loading = false; this.cdr.detectChanges(); return; }
          this.orderInfo = new InfoOrders(data);
          this.searchService.passOrderInfoValue(search);
          this.dynamicLink['search'] = search;
          this.dynamicLink['data'] = this.formattedDate;
          this.cdr.detectChanges();
        },
        error: (err) => {
          Log.error('[Dashboard] getInfoOrders search failed', err);
          this.loading = false;
          this.cdr.detectChanges();
        },
      });

    this.ordersServ.getInfo(startDate)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          if (!data) { this.loading = false; this.cdr.detectChanges(); return; }
          this.otherInfo = new Info(data);
          this.searchService.passInfoValue(search);
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          Log.error('[Dashboard] getInfo search failed', err);
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  // ─── Historical lookup selectors ────────────────────────────────
  /**
   * Historical month-picker change. PURE LOCAL: only re-derives the
   * "Riepilogo storico" cards. NEVER touches goalService or the
   * "Oggi" cards. Selecting Aprile 2024 doesn't affect today's
   * dashboard reading.
   */
  onMonthChange(): void {
    this.recomputeAllSums();
  }

  onDateChange(): void {
    this.recomputeAllSums();
  }

  // ─── Initial new-orders fetch (own session counter) ─────────────
  getNewOrders(): void {
    this.loading = true;
    this.ordersServ.getNewOrders()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ data }) => {
          this.gridData = data || [];
          this.netValue = 0;
          this.etValuePartial = 0;
          this.etValueTotal = 0;
          this.etValueBlocked = 0;
          this.etMonthlyOrders = 0;
          for (const order of this.gridData) {
            const value = order.or_net_value || 0;
            this.netValue += value;
            if (order['or_partial'] === 'S') this.etValuePartial += value;
            else this.etValueTotal += value;
            if (order['tab_order_status_desc'] === 'Blocca') this.etValueBlocked += value;
            if (order['tab_order_status_desc'] === 'Assegnato') this.etMonthlyOrders += value;
          }
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          Log.error('[Dashboard] getNewOrders failed', err);
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  // ─── Display helpers ────────────────────────────────────────────
  /** Label for the selected month/year (used in the historical card). */
  get selectedMonthLabel(): string {
    return `${this.months[this.selectedMonth].name} ${this.selectedYear}`;
  }

  /** Status pill color for the forecast card. */
  get forecastStatus(): 'good' | 'warning' | 'danger' {
    if (this.forecastPercent >= 100) return 'good';
    if (this.forecastPercent >= 80) return 'warning';
    return 'danger';
  }

  getDailyMotivationalMessage(): string {
    if (!this.todayDailyGoal) return '✅ Nessun obiettivo giornaliero attivo. Goditi la giornata!';
    const progress = this.todayDailyProgress || 0;
    const hour = new Date().getHours();
    if (progress >= 100) return '🎉 Obiettivo giornaliero raggiunto! Fantastico lavoro!';
    if (hour < 12) {
      if (progress < 30) return '☀️ Buongiorno! Hai tutta la mattina per recuperare, forza!';
      if (progress < 70) return '🌅 Ottimo risveglio, continua così!';
      return '🔥 Sei già a buon punto, mantieni il ritmo!';
    }
    if (hour < 17) {
      if (progress < 50) return '⏰ Il pomeriggio è iniziato, devi accelerare!';
      if (progress < 90) return '💪 Sei sulla buona strada, non mollare!';
      return '🚀 Sei quasi arrivato, dai tutto!';
    }
    if (progress < 70) return '⚠️ Sei ancora indietro, manca poco alla fine della giornata!';
    if (progress < 100) return '🔥 Ultimi sforzi, puoi farcela!';
    return '🎉 Hai completato l\'obiettivo, goditi la serata!';
  }

  getMonthlyMotivationalMessage(): string {
    if (!this.currentMonthGoal || this.currentMonthGoal <= 0) return '';
    const remainingGoal = this.currentMonthGoal - this.currentMonthEarned;
    if (remainingGoal <= 0) return '🎉 Obiettivo mensile raggiunto o superato! Goditi il successo!';

    const totalWorkDays =
      this.golServ.getWorkingDays(this.todayYear, this.todayMonth) ||
      this.weekdaysInMonth(this.todayYear, this.todayMonth);
    const pastWorkDays = this.countWorkingDaysPassed(
      this.todayYear,
      this.todayMonth,
      this.today.getDate(),
    );
    const remainingDays = Math.max(0, totalWorkDays - pastWorkDays);
    const formatted = this.formatEUR(remainingGoal);

    if (remainingDays > 10) return `📅 Hai ancora ${remainingDays} giorni lavorativi per raggiungere i ${formatted}.`;
    if (remainingDays > 3)  return `⚡ Rimangono ${remainingDays} giorni per raggiungere i ${formatted}. Aumenta il ritmo!`;
    if (remainingDays === 1) return `🔥 Ultimo giorno per arrivare a ${formatted}! Dai tutto!`;
    if (remainingDays > 1)  return `🔥 Ultimi ${remainingDays} giorni per arrivare a ${formatted}!`;
    return '💪 Continua così, ogni giorno conta!';
  }

  // ─── Confetti (single-fire guarded in recomputeAllProgress) ────
  launchConfetti(): void {
    const duration = 4_000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 1000 };
    const interval: any = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) {
        clearInterval(interval);
        return;
      }
      const particleCount = 50 * (timeLeft / duration);
      confetti(
        Object.assign({}, defaults, {
          particleCount,
          origin: { x: Math.random(), y: Math.random() - 0.2 },
        }),
      );
    }, 250);
  }

  // ─── Internal helpers ──────────────────────────────────────────
  private countWorkingDaysPassed(year: number, month: number, day: number): number {
    if (day < 1) return 0;
    let count = 0;
    for (let d = 1; d <= day; d++) {
      const dow = new Date(year, month, d).getDay();
      if (dow !== 0 && dow !== 6) count++;
    }
    return count;
  }

  private weekdaysInMonth(year: number, month: number): number {
    const days = new Date(year, month + 1, 0).getDate();
    return this.countWorkingDaysPassed(year, month, days);
  }

  private safePercent(numerator: number, denominator: number): number {
    if (!denominator || denominator === 0 || !Number.isFinite(denominator)) return 0;
    if (!Number.isFinite(numerator)) return 0;
    return (numerator / denominator) * 100;
  }

  private formatEUR(value: number): string {
    return (value ?? 0).toLocaleString('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  
}
